import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import VirtualList from "../../components/ui/VirtualList";
import {
  formatCount,
  getAncestors,
  jobsOfFunction,
  matchRanges,
  rankEntries,
  tokenize,
} from "./orgChartModel";

// Search across every job and function in the company. Results are ranked
// and windowed, so a query matching thousands of jobs renders a screenful.
// Picking a job reveals it in the chart; picking a function highlights its
// jobs in the chart and lists them here.
const DEBOUNCE_MS = 140;
const PREVIEW_FUNCTIONS = 4;
const ROW_HEIGHT = { header: 30, function: 54, job: 58, more: 36, hint: 64 };
const rowHeight = (row) => ROW_HEIGHT[row.type];
const PANEL_MAX_H = 440;

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function Highlight({ text, tokens }) {
  const ranges = matchRanges(text, tokens);
  if (ranges.length === 0) return text;
  const out = [];
  let cursor = 0;
  ranges.forEach(([s, e]) => {
    if (s > cursor) out.push(text.slice(cursor, s));
    out.push(<mark key={s}>{text.slice(s, e)}</mark>);
    cursor = e;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

function reportingPath(index, id) {
  const names = getAncestors(index, id).map((a) => index.byId.get(a)?.name || "Untitled");
  if (names.length <= 3) return names.join(" › ");
  return `${names[0]} › … › ${names.slice(-2).join(" › ")}`;
}

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const isSelectable = (row) => row.type === "function" || row.type === "job" || row.type === "more";

function OrgSearch({ index, searchIndex, avatarGradient, focusedFunctionKey, focusedJob, onPickJob, onPickFunction, onClearFunction, onClearJob }) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const savedQuery = useRef("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [scopeKey, setScopeKey] = useState(null);
  const [active, setActive] = useState(-1);
  const [scrollTarget, setScrollTarget] = useState(null);
  const debounced = useDebounced(query, DEBOUNCE_MS);
  const tokens = useMemo(() => tokenize(debounced), [debounced]);
  const scopeFn = scopeKey ? searchIndex.functionByKey.get(scopeKey) : null;

  const { rows, jobCount, fnCount } = useMemo(() => {
    if (scopeFn) {
      const members = jobsOfFunction(searchIndex, scopeFn.key);
      const jobs = tokens.length ? rankEntries(members, debounced) : members;
      const list = jobs.map((entry) => ({ type: "job", key: entry.key, entry }));
      if (list.length === 0) {
        list.push({ type: "hint", key: "hint", text: members.length ? "No jobs in this function match your search." : "This function has no jobs yet." });
      }
      return { rows: list, jobCount: jobs.length, fnCount: 0 };
    }

    if (tokens.length === 0) {
      if (tab === "jobs") {
        return {
          rows: [{ type: "hint", key: "hint", text: `Type a job title, code or employee to search ${formatCount(searchIndex.jobs.length)} jobs.` }],
          jobCount: searchIndex.jobs.length,
          fnCount: searchIndex.functions.length,
        };
      }
      const list = [{ type: "header", key: "h-browse", label: "Browse by function", count: searchIndex.functions.length }];
      searchIndex.functions.forEach((entry) => list.push({ type: "function", key: `f-${entry.key}`, entry }));
      if (searchIndex.functions.length === 0) {
        list.push({ type: "hint", key: "hint", text: "No functions are defined for this company." });
      }
      return { rows: list, jobCount: searchIndex.jobs.length, fnCount: searchIndex.functions.length };
    }

    const fns = tab === "jobs" ? [] : rankEntries(searchIndex.functions, debounced);
    const jobs = tab === "functions" ? [] : rankEntries(searchIndex.jobs, debounced);
    const list = [];
    if (tab === "all" && fns.length > 0) {
      list.push({ type: "header", key: "h-fn", label: "Functions", count: fns.length });
      fns.slice(0, PREVIEW_FUNCTIONS).forEach((entry) => list.push({ type: "function", key: `f-${entry.key}`, entry }));
      if (fns.length > PREVIEW_FUNCTIONS) {
        list.push({ type: "more", key: "more-fn", label: `View all ${formatCount(fns.length)} matching functions` });
      }
    } else if (tab === "functions") {
      fns.forEach((entry) => list.push({ type: "function", key: `f-${entry.key}`, entry }));
    }
    if (tab === "all" && jobs.length > 0) {
      list.push({ type: "header", key: "h-job", label: "Jobs", count: jobs.length });
    }
    jobs.forEach((entry) => list.push({ type: "job", key: entry.key, entry }));
    if (list.length === 0) {
      list.push({ type: "hint", key: "hint", text: `No ${tab === "all" ? "jobs or functions" : tab} match “${debounced.trim()}”.` });
    }
    return {
      rows: list,
      jobCount: tab === "functions" ? rankEntries(searchIndex.jobs, debounced).length : jobs.length,
      fnCount: tab === "jobs" ? rankEntries(searchIndex.functions, debounced).length : fns.length,
    };
  }, [searchIndex, scopeFn, tokens, debounced, tab]);

  useEffect(() => {
    setActive(-1);
    setScrollTarget(null);
  }, [rows]);

  // Close on outside click; "/" focuses the search from anywhere on the page.
  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const editable = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if (editable || document.querySelector(".joh-modal-backdrop")) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const enterScope = (entry) => {
    savedQuery.current = query;
    setScopeKey(entry.key);
    setQuery("");
    onPickFunction(entry.key);
    inputRef.current?.focus();
  };

  const leaveScope = () => {
    setScopeKey(null);
    setQuery(savedQuery.current);
    inputRef.current?.focus();
  };

  const activate = (row) => {
    if (row.type === "job") {
      onPickJob(row.entry.id);
      setOpen(false);
    } else if (row.type === "function") {
      enterScope(row.entry);
    } else if (row.type === "more") {
      setTab("functions");
    }
  };

  const step = (dir) => {
    let i = active >= 0 ? active : dir > 0 ? -1 : rows.length;
    for (let n = 0; n < rows.length; n++) {
      i = (i + dir + rows.length) % rows.length;
      if (isSelectable(rows[i])) {
        setActive(i);
        setScrollTarget(i);
        return;
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      step(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      step(-1);
    } else if (e.key === "Enter") {
      const row = rows[active] || rows.find(isSelectable);
      if (open && row && isSelectable(row)) {
        e.preventDefault();
        activate(row);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (scopeFn) leaveScope();
      else if (open) setOpen(false);
      else inputRef.current?.blur();
    } else if (e.key === "Backspace" && scopeFn && !query) {
      leaveScope();
    }
  };

  const renderRow = (row, i, style) => {
    const cls = `joh-sr-row ${row.type}${i === active ? " active" : ""}`;
    if (row.type === "header") {
      return (
        <div key={row.key} className={cls} style={style}>
          <span>{row.label}</span>
          <span className="joh-sr-header-count">{formatCount(row.count)}</span>
        </div>
      );
    }
    if (row.type === "hint") {
      return <div key={row.key} className={cls} style={style}>{row.text}</div>;
    }
    const common = {
      id: `joh-sr-${i}`,
      role: "option",
      "aria-selected": i === active,
      className: cls,
      style,
      onMouseMove: () => { if (i !== active) setActive(i); },
      onMouseDown: (e) => e.preventDefault(),
      onClick: () => activate(row),
    };
    if (row.type === "more") {
      return <div key={row.key} {...common}>{row.label} →</div>;
    }
    if (row.type === "function") {
      const fn = row.entry;
      const focused = fn.key === focusedFunctionKey;
      return (
        <div key={row.key} {...common}>
          <span className="joh-sr-fn-icon" style={fn.color ? { boxShadow: `inset 0 0 0 2px ${fn.color}` } : undefined} aria-hidden="true">
            {initials(fn.name)}
          </span>
          <span className="joh-sr-text">
            <span className="joh-sr-title">
              <Highlight text={fn.name} tokens={tokens} />
              {focused && <span className="joh-sr-badge focus">Highlighted</span>}
            </span>
            <span className="joh-sr-sub">
              {fn.path.length > 0 ? fn.path.join(" › ") : fn.code || "Top-level function"}
            </span>
          </span>
          <span className="joh-sr-count">{formatCount(fn.jobCount)} {fn.jobCount === 1 ? "job" : "jobs"}</span>
          <span className="joh-sr-chevron" aria-hidden="true">›</span>
        </div>
      );
    }
    const job = row.entry;
    const [g1, g2] = avatarGradient(job.name);
    const path = job.inTree ? reportingPath(index, job.id) : "";
    return (
      <div key={row.key} {...common}>
        <span className="joh-sr-avatar" style={{ background: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }} aria-hidden="true">
          {initials(job.name)}
        </span>
        <span className="joh-sr-text">
          <span className="joh-sr-title">
            <Highlight text={job.name} tokens={tokens} />
            {job.code && <span className="joh-sr-code">{job.code}</span>}
          </span>
          <span className="joh-sr-sub">
            {job.functionName && !scopeFn && <span className="joh-sr-fn-chip">{job.functionName}</span>}
            {job.inTree ?
              <span className="joh-sr-path" title={path ? `Reports through: ${path}` : "Top of the organization"}>
                {path || "Top of the organization"}
              </span>
            : <span className="joh-sr-badge warn">Unassigned</span>}
            {job.employee && <span className="joh-sr-employee">· {job.employee}</span>}
          </span>
        </span>
      </div>
    );
  };

  const counts = tokens.length > 0 && !scopeFn;
  const tabs = [
    { id: "all", label: "All" },
    { id: "jobs", label: "Jobs", count: jobCount },
    { id: "functions", label: "Functions", count: fnCount },
  ];
  const focusedFn = focusedFunctionKey ? searchIndex.functionByKey.get(focusedFunctionKey) : null;

  return (
    <div className="joh-search" ref={rootRef}>
      <div className={`joh-search-field${open ? " open" : ""}`}>
        <svg className="joh-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        {scopeFn && (
          <button type="button" className="joh-search-scope" onClick={leaveScope} title="Back to all results">
            {scopeFn.name} <span aria-hidden="true">✕</span>
          </button>
        )}
        <input
          ref={inputRef}
          id="joh-org-search"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="joh-org-search-results"
          aria-activedescendant={open && active >= 0 ? `joh-sr-${active}` : undefined}
          autoComplete="off"
          placeholder={scopeFn ? `Search ${formatCount(scopeFn.jobCount)} jobs in ${scopeFn.name}…` : `Search ${formatCount(searchIndex.jobs.length)} jobs or functions…`}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query ?
          <button type="button" className="joh-search-clear" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label="Clear search">✕</button>
        : !open && <kbd className="joh-search-kbd" title="Press / to search">/</kbd>}
      </div>

      {!open && (focusedJob || focusedFn) && (
        <div className="joh-search-focus-chips">
          {focusedJob && (
            <div className="joh-search-focus-chip">
              <span>Highlighting <strong>{focusedJob.name || "Untitled"}</strong></span>
              <button type="button" onClick={onClearJob} aria-label="Clear job highlight" title="Clear highlight">✕</button>
            </div>
          )}
          {focusedFn && (
            <div className="joh-search-focus-chip">
              <span>Highlighting <strong>{focusedFn.name}</strong></span>
              <button type="button" onClick={onClearFunction} aria-label="Clear function highlight" title="Clear highlight">✕</button>
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="joh-search-panel">
          {scopeFn ? (
            <div className="joh-search-scope-head">
              <button type="button" className="joh-search-back" onClick={leaveScope}>‹ All results</button>
              <div className="joh-search-scope-title">
                <strong>{scopeFn.name}</strong>
                <span>{formatCount(scopeFn.jobCount)} {scopeFn.jobCount === 1 ? "job" : "jobs"}{scopeFn.path.length ? ` · ${scopeFn.path.join(" › ")}` : ""}</span>
              </div>
              {scopeFn.key === focusedFunctionKey ?
                <button type="button" className="joh-search-scope-action" onClick={onClearFunction}>Clear highlight</button>
              : <button type="button" className="joh-search-scope-action" onClick={() => onPickFunction(scopeFn.key)}>Highlight</button>}
            </div>
          ) : (
            <div className="joh-search-tabs" role="tablist">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={`joh-search-tab${tab === t.id ? " active" : ""}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                  {counts && t.count != null && <span className="joh-search-tab-count">{formatCount(t.count)}</span>}
                </button>
              ))}
            </div>
          )}

          <VirtualList
            id="joh-org-search-results"
            role="listbox"
            className="joh-search-results"
            items={rows}
            itemHeight={rowHeight}
            maxHeight={PANEL_MAX_H}
            padding={6}
            resetKey={`${scopeKey}|${tab}|${debounced}`}
            scrollToIndex={scrollTarget}
            renderItem={renderRow}
          />

          <div className="joh-search-footer">
            <span>
              {scopeFn ?
                `${formatCount(jobCount)} job${jobCount === 1 ? "" : "s"}${tokens.length ? " match" : ""}`
              : tokens.length ?
                `${formatCount(jobCount)} jobs · ${formatCount(fnCount)} functions`
              : `${formatCount(searchIndex.jobs.length)} jobs · ${formatCount(searchIndex.functions.length)} functions`}
            </span>
            <span className="joh-search-keys"><kbd>↑</kbd><kbd>↓</kbd> navigate <kbd>↵</kbd> open <kbd>esc</kbd> {scopeFn ? "back" : "close"}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(OrgSearch);
